import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { RiHotelFill } from "react-icons/ri";
import { FaPlane } from "react-icons/fa";
import { MdOutlineDirectionsBus } from "react-icons/md";
import { IoBoat } from "react-icons/io5";
import {
  HOTELS,
  PACKAGES,
  PLANES,
  ORGANIZED_TOURS,
  useSearch,
  CRUISE_PAYMENT,
  CAMPAIGNS,
} from "../../../../contexts/SearchContext";
import { ROUTES } from "../../../../Routes/Routes";
import { SHOW_FLIGHT_BOOKING_MOCKUP_IN_SEARCHOFFER_PLANES_TAB } from "../../../../config/flightBookingMockupSearchOffer";
import { useAgentEntitlements } from "../../../../features/agentEntitlements/useAgentEntitlements";
import { isAdminUser } from "../../../../services/authService";
import { Megaphone } from "lucide-react";
import SearchParamsLockButton from "../SubComponents/SearchParamsLockButton/SearchParamsLockButton";
import "./MobileCategoryPicker.css";

function getCategories(t) {
  // G.4E — Dynamic Packages removed from active product picker.
  const base = [
    {
      id: PACKAGES,
      title: t("searchagent.packages"),
      icon: <RiHotelFill style={{ fontSize: "1.1em" }} />,
    },
    {
      id: HOTELS,
      title: t("searchagent.hotels"),
      icon: <RiHotelFill style={{ fontSize: "1.1em" }} />,
    },
    {
      id: ORGANIZED_TOURS,
      title: t("searchagent.organizedtours"),
      icon: <MdOutlineDirectionsBus style={{ fontSize: "1.1em" }} />,
    },
    {
      id: PLANES,
      title: t("searchagent.planes"),
      icon: <FaPlane style={{ fontSize: "1.1em" }} />,
    },
    {
      id: CAMPAIGNS,
      title: t("searchagent.campaigns", { defaultValue: "קמפיינים" }),
      icon: <Megaphone size={18} strokeWidth={2.25} aria-hidden />,
    },
    {
      id: CRUISE_PAYMENT,
      title: t("תשלום מנו"),
      icon: <IoBoat style={{ fontSize: "1.1em" }} />,
    },
  ];

  return base;
}

function MobileCategoryPicker({ variant = "default" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchTab, setSearchTab: ogSetSearchTab } = useSearch();
  const {
    canSearchHotels,
    canSearchPackages,
    canSearchFlights,
    canOrganizedTours,
    status: entitlementsStatus,
  } = useAgentEntitlements();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const prevOverflowRef = useRef(null);

  /** מסננים לפי SSOT רק כשיש מצב הסמכה או אדמין (selectors עם הרפיה בטעינה) */
  const entitlementsGateActive =
    entitlementsStatus === "ok" ||
    entitlementsStatus === "error" ||
    isAdminUser();

  const categories = useMemo(() => {
    const all = getCategories(t);
    if (!entitlementsGateActive) return all;
    return all.filter((c) => {
      if (c.id === HOTELS && !canSearchHotels) return false;
      if (c.id === PACKAGES && !canSearchPackages) return false;
      if (c.id === PLANES && !canSearchFlights) return false;
      if (c.id === ORGANIZED_TOURS && !canOrganizedTours) return false;
      return true;
    });
  }, [
    t,
    entitlementsGateActive,
    canSearchHotels,
    canSearchPackages,
    canSearchFlights,
    canOrganizedTours,
  ]);

  const fallbackCategories = getCategories(t);
  const current =
    categories.find((c) => c.id === searchTab) ||
    categories[0] ||
    fallbackCategories[0];

  useEffect(() => {
    if (!entitlementsGateActive || !categories.length) return;
    const ok = categories.some((c) => c.id === searchTab);
    if (!ok) {
      ogSetSearchTab(categories[0].id, { source: "context_sync" });
      navigate("/p/dashboard/searchoffer");
    }
  }, [entitlementsGateActive, categories, searchTab, navigate, ogSetSearchTab]);

  const selectTab = (tab) => {
    if (tab === PLANES) {
      if (entitlementsGateActive && !canSearchFlights) return;
      ogSetSearchTab(PLANES, { source: "user_click" });
      if (SHOW_FLIGHT_BOOKING_MOCKUP_IN_SEARCHOFFER_PLANES_TAB) {
        navigate("/p/dashboard/searchoffer");
      } else {
        navigate(`/${ROUTES.FLIGHT_BOOKING}`);
      }
      setOpen(false);
      return;
    }
    if (tab === CRUISE_PAYMENT) {
      navigate("/p/dashboard/cruise-payment");
      ogSetSearchTab(CRUISE_PAYMENT, { source: "user_click" });
      setOpen(false);
      return;
    }
    if (tab === ORGANIZED_TOURS) {
      if (entitlementsGateActive && !canOrganizedTours) return;
    }
    ogSetSearchTab(tab, { source: "user_click" });
    navigate("/p/dashboard/searchoffer");
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    prevOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflowRef.current || "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  return (
    <>
      <div
        className={[
          "mobile-category-picker-block",
          variant === "secondary" ? "mobile-category-picker-block--secondary" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className={[
            "mobile-category-picker-trigger",
            variant === "secondary"
              ? "mobile-category-picker-trigger--secondary"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setOpen(true)}
          aria-label={`בחר קטגוריה. נבחר: ${current.title}`}
          aria-expanded={open}
        >
          <span className="mobile-category-picker-trigger-icon">
            {current.icon}
          </span>
          <span className="mobile-category-picker-trigger-text">
            קטגוריה: {current.title}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="mobile-category-picker-trigger-chevron"
          />
        </button>
        <SearchParamsLockButton className="mobile-category-picker-lock" />
      </div>

      {open &&
        createPortal(
          <>
            <div
              className="mobile-category-picker-overlay"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              ref={panelRef}
              className="mobile-category-picker-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobile-category-picker-title"
              tabIndex={-1}
            >
              <div className="mobile-category-picker-sheet-header">
                <h2 id="mobile-category-picker-title" className="mobile-category-picker-sheet-title">
                  בחר קטגוריה
                </h2>
                <button
                  type="button"
                  className="mobile-category-picker-sheet-close"
                  onClick={() => setOpen(false)}
                  aria-label="סגור"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="mobile-category-picker-sheet-list">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`mobile-category-picker-row ${searchTab === cat.id ? "selected" : ""}`}
                    onClick={() => selectTab(cat.id)}
                  >
                    <span className="mobile-category-picker-row-icon">{cat.icon}</span>
                    <span className="mobile-category-picker-row-title">{cat.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

export default MobileCategoryPicker;
